import { Ellipsis } from '../../icons/lucide/Ellipsis';
import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { cardActionsA11yProps } from '../../utils/cardActionsA11y';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { CardChipTooltip } from '../cards/CardChipTooltip';
import { CARD_NATIVE_SHADOW } from '../cards/cardShadows';
import { MarqueeLabel } from '../MarqueeLabel';
import type { PractiseCardProps } from './PractiseCard.types';
import { PRACTISE_TYPE_META, practiseCardVariants } from './PractiseCard.variants';

/** Title typography per view: grid L1 (Bold 16/20), list H3 (SemiBold 18/26). */
const TITLE_CLASS = {
  grid: 'min-w-0 flex-1 text-l1 font-bold text-secondary-950 native:font-mona-bold',
  list: 'min-w-0 flex-1 text-h3 font-semibold text-secondary-950 native:font-mona-semibold',
} as const;

/**
 * Cross-platform PractiseCard — the design-system card for a practise activity
 * ("Quiz cards" in Figma). Renders identically on web and React Native.
 *
 * Mirrors the Figma "Flashka Design System" file `YZK9r3wDhvw1pY60KlVEIj`,
 * "Quiz cards updated" frame (node `3153:44675`): a flat card carrying a
 * colour-tinted type icon (`quiz` / `audio` / `exam` / `caseStudy`), a title,
 * a date, and a progress-status pill (green `completed` / orange
 * `in-progress`). Grid = 181-wide tile; list = full-width row. The avatar
 * facepile + subject badge in the frame are intentionally out of scope.
 *
 * @example
 * ```tsx
 * import { PractiseCard } from '@flashka/shared/ui';
 *
 * <PractiseCard
 *   view="grid"
 *   type="quiz"
 *   title="Brain Anatomy"
 *   dateText="26 Nov"
 *   status={{ tone: 'completed', label: t('practise.score', { pct: 90 }) }}
 *   onPress={openActivity}
 *   onActionsPress={openMenu}
 *   actionsLabel="Quiz actions"
 * />
 * ```
 */
export function PractiseCard({
  view,
  type,
  typeLabel,
  title,
  dateText,
  status,
  onPress,
  onActionsPress,
  actionsLabel,
  actionsSlot,
  disabled = false,
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
}: PractiseCardProps) {
  const resolvedView = view ?? 'grid';
  const { colour, icon } = PRACTISE_TYPE_META[type];

  // Web shadows come from `web:shadow-card-row`; native needs the RN shadow
  // props (NativeWind can't compile box-shadow). Both views use the same flat
  // "Shadow-Secondary", so the list native shadow fits grid too. Web Pressable
  // ignores nativeProps entirely, so merging unconditionally is safe.
  const mergedNativeProps = {
    ...nativeProps,
    // The ⋯ reachable to a SCREEN READER. Same defect and same fix as
    // `DeckCard`: the container below is ONE accessibility element on iOS, which
    // hid the ⋯ entirely. Native-only — the web Pressable discards
    // `nativeProps`, and web already exposes the ⋯ as a real nested <button>.
    ...cardActionsA11yProps({ onActionsPress, actionsLabel, disabled }),
    style: [
      CARD_NATIVE_SHADOW.list,
      (nativeProps as { style?: unknown } | undefined)?.style,
    ],
  };

  // Colour-tinted type icon chip — the shared Badge `icon` type at size `s`
  // (28×28, rounded-8, 14px icon), tint driven by the type's colour. Wrapped in
  // the card-family tooltip so the type name shows on hover (web) / rides the
  // a11y name — `CardChipTooltip` is a no-op without a label and on native.
  const typeBadge = (
    <CardChipTooltip label={typeLabel}>
      <Badge
        type="icon"
        size="s"
        colour={colour}
        icon={icon}
        accessibilityLabel={typeLabel}
      />
    </CardChipTooltip>
  );

  // Trailing ⋯ — Figma's transparent "Icon Button" (28×28, 14px ellipsis) =
  // the shared ghost Button at `xxs`. Stop the press from bubbling into the
  // card's own onPress. `actionsSlot` replaces the button when the caller's
  // menu library must own the trigger element (web Radix Trigger asChild).
  const actions =
    actionsSlot ??
    (onActionsPress ? (
      <Button
        variant="ghost"
        size="xxs"
        icon={Ellipsis}
        disabled={disabled}
        accessibilityLabel={actionsLabel}
        testID={subTestID(testID, 'menu')}
        onPress={(event: PressEvent) => {
          event?.stopPropagation?.();
          onActionsPress(event);
        }}
      />
    ) : null);

  // Progress-status pill — the shared Badge label chip; tone → colour
  // (completed = green `success`, in-progress = orange `warning`).
  const statusBadge = status ? (
    <Badge
      type="label"
      size="s"
      colour={status.tone === 'completed' ? 'success' : 'warning'}>
      {status.label}
    </Badge>
  ) : null;

  // Date chip — the FIRST badge of the row (Figma 5714:30710 list /
  // 5714:30736 grid): the neutral label Badge (`bg-secondary-50` /
  // `text-secondary-600`), not the old muted right-aligned text.
  const dateBadge = dateText ? (
    <Badge type="label" size="s" colour="neutral" style="glass">
      {dateText}
    </Badge>
  ) : null;

  // Badges row, shared by both views: date chip first, then the status pill,
  // left-aligned at the DS 4px chip gap.
  const bottomRow =
    statusBadge || dateBadge ? (
      <View className="w-full flex-row items-center gap-1">
        {dateBadge}
        {statusBadge}
      </View>
    ) : null;

  // `as="div"` on web: the card contains a real <button> (the actions ghost
  // Button) — nesting it inside another <button> would be invalid HTML.
  const containerProps = {
    as: 'div',
    className: cn(practiseCardVariants({ view: resolvedView }), className),
    onPress,
    // Light tick on touch-down, matching the other DS cards (native only).
    haptic: true,
    disabled,
    accessibilityRole: 'button',
    accessibilityLabel: accessibilityLabel ?? title,
    testID,
    webProps,
    nativeProps: mergedNativeProps,
  } as const;

  if (resolvedView === 'list') {
    return (
      <Pressable {...containerProps}>
        {/* Top row: type icon + title (8px apart — Figma 5714:30712) as one
            group, ⋯ actions 4px after it. */}
        <View className="w-full flex-row items-center gap-1">
          <View className="min-w-0 flex-1 flex-row items-center gap-2">
            {typeBadge}
            <MarqueeLabel
              className={TITLE_CLASS.list}
              label={title}
              testID={subTestID(testID, 'title')}
            />
          </View>
          {actions}
        </View>
        {/* Badges row: date chip, then the status pill. */}
        {bottomRow}
      </Pressable>
    );
  }

  // Grid: fixed-height tile — type icon + ⋯ on the first row, the title in the
  // middle, then the status pill + date sharing the last row (`justify-between`
  // spreads the three rows so every card is the same height). The avatar
  // facepile that shared the design's date row is out of scope.
  return (
    <Pressable {...containerProps}>
      <View className="w-full flex-row items-center justify-between">
        {typeBadge}
        {actions}
      </View>
      <View className="w-full flex-row items-center">
        <MarqueeLabel
          className={TITLE_CLASS.grid}
          label={title}
          testID={subTestID(testID, 'title')}
        />
      </View>
      {bottomRow}
    </Pressable>
  );
}
(PractiseCard as { displayName?: string }).displayName = 'PractiseCard';
